import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Loader2, ClipboardList, Sparkles, TrendingUp, AlertTriangle, FileText, History } from 'lucide-react';
import api from '@/lib/api';
import ChantierQualificationDocumentsTab from './ChantierQualificationDocumentsTab';

/**
 * Panneau détaillé Qualification Travaux d’optimisation (spec §21-24).
 * Onglets : Synthèse · Réponses · Documents · Historique.
 * Ouvert depuis le bloc compact via "Voir détail".
 */
const ANSWER_LABELS = {
    chauffage_type: 'Type de chauffage',
    chauffage_energie: 'Énergie du chauffage',
    chauffage_age: 'Âge du chauffage',
    chauffage_remplacement_envisage: 'Remplacement du chauffage envisagé',
    chauffage_pannes: 'Pannes chauffage récentes',
    climatisation_present: 'Climatisation présente',
    groupes_froids_present: 'Groupes froids présents',
    groupes_froids_usages: 'Usages des groupes froids',
    groupes_froids_age: 'Âge des groupes froids',
    groupes_froids_forte_consommation: 'Forte consommation groupes froids',
    groupes_froids_pannes: 'Pannes groupes froids',
    groupes_froids_remplacement_envisage: 'Remplacement groupes froids envisagé',
    recuperation_chaleur_envisageable: 'Récupération de chaleur envisageable',
    regulation_hp_bp_a_etudier: 'Régulation HP/BP à étudier',
    facture_elec_mensuelle: 'Facture électrique mensuelle',
    facture_gaz_mensuelle: 'Facture gaz mensuelle',
    facture_elec_montant_precis: 'Montant précis facture élec',
    facture_gaz_montant_precis: 'Montant précis facture gaz',
    surface_chauffee: 'Surface chauffée',
    hauteur_sous_plafond: 'Hauteur sous plafond',
    hauteur_sous_plafond_approximative: 'Hauteur sous plafond',
    horaires_utilisation: "Horaires d'utilisation",
    site_year_round: 'Site utilisé toute l\'année',
    multi_sites: 'Multi-sites',
    propriete_status: 'Statut de propriété',
    contact_est_decideur: 'Contact = décideur',
    decret_tertiaire: 'Décret tertiaire',
    geothermie_terrain: 'Terrain pour géothermie',
    travaux_deja_realises: 'Travaux déjà réalisés',
    travaux_types: 'Types de travaux réalisés',
    travaux_subventions_utilisees: 'Subventions mobilisées pour ces travaux ?',
    travaux_subventions_types: 'Subventions mobilisées',
    besoin_identifie: 'Besoin identifié',
    projet_temporalite: 'Temporalité du projet',
};

const VALUE_LABELS = {
    // Chauffage type
    chaudiere_fioul: 'Chaudière fioul',
    chaudiere_gaz: 'Chaudière gaz',
    electrique: 'Électrique',
    pompe_a_chaleur: 'Pompe à chaleur',
    biomasse: 'Biomasse',
    reseau_chaleur: 'Réseau de chaleur',
    plusieurs: 'Plusieurs sources',
    // Ages
    plus_15_ans: 'Plus de 15 ans',
    '10_15_ans': '10 à 15 ans',
    '5_10_ans': '5 à 10 ans',
    moins_5_ans: 'Moins de 5 ans',
    plus_10_ans: 'Plus de 10 ans',
    // Hauteur sous plafond
    moins_3m: 'Moins de 3 m',
    '3_4m': '3 à 4 m',
    '4_6m': '4 à 6 m',
    plus_6m: 'Plus de 6 m',
    standard: 'Standard (~3 m)',
    haut: 'Haut',
    tres_haut: 'Très haut',
    je_ne_sais_pas: 'Ne sait pas',
    // Travaux types
    isolation_toiture: 'Isolation toiture',
    isolation_murs: 'Isolation murs',
    isolation_planchers: 'Isolation planchers',
    changement_chaudiere: 'Changement de chaudière',
    pac: 'Pompe à chaleur',
    eclairage_led: 'Éclairage LED',
    gtb_gtc: 'GTB / GTC',
    calorifugeage: 'Calorifugeage',
    vmc_double_flux: 'VMC double flux',
    photovoltaique: 'Photovoltaïque',
    // Subventions
    cee: 'CEE',
    maprimerenov: 'MaPrimeRénov\'',
    ademe: 'ADEME',
    eco_ptz: 'Éco-PTZ',
    regionales: 'Aides régionales',
    credit_impot: 'Crédit d\'impôt',
    autre: 'Autre',
    // Factures
    moins_2000: '< 2 000 €',
    '2000_5000': '2 000 - 5 000 €',
    '5000_10000': '5 000 - 10 000 €',
    plus_10000: '> 10 000 €',
    // Propriété
    proprietaire: 'Propriétaire',
    locataire: 'Locataire',
    mixte: 'Mixte',
    // Décret tertiaire
    concerne: 'Concerné',
    inscrit_operat_incomplet: 'Inscrit OPERAT (incomplet)',
    aucune_declaration: 'Aucune déclaration',
    // Généraux
    oui: 'Oui', non: 'Non', mixte_partie: 'Propriétaire d\'une partie',
    moins_3_mois: '< 3 mois', '3_6_mois': '3-6 mois', '6_12_mois': '6-12 mois', plus_12_mois: '> 12 mois',
    // Usages groupes froids
    cave_viticole: 'Cave viticole',
    process_industriel: 'Process industriel',
    chambres_froides_negatives: 'Chambres froides négatives',
    chambres_froides_positives: 'Chambres froides positives',
    entrepot_frigorifique: 'Entrepôt frigorifique',
    conservation_alimentaire: 'Conservation alimentaire',
    data_center: 'Data center',
    climatisation_grande_puissance: 'Climatisation grande puissance',
    chaudiere_ancienne: 'Chaudière ancienne',
};

const labelValue = (v) => {
    if (v === true) return 'Oui';
    if (v === false) return 'Non';
    if (v == null || v === '') return '—';
    if (Array.isArray(v)) return v.map((x) => VALUE_LABELS[x] || x).join(' · ');
    return VALUE_LABELS[v] || String(v);
};
const labelKey = (k) => ANSWER_LABELS[k] || k.replace(/_/g, ' ');

const NOTE_TONE = (n) => n >= 4 ? 'bg-emerald-600 text-white'
    : n >= 3 ? 'bg-blue-600 text-white'
    : n >= 2 ? 'bg-amber-500 text-white'
    : 'bg-slate-500 text-white';

export default function ChantierQualificationDetailDialog({ open, onOpenChange, qualification, currentUser, canEdit = false }) {
    const [history, setHistory] = useState([]);
    const [loadingHist, setLoadingHist] = useState(false);
    const q = qualification;

    useEffect(() => {
        if (!open || !q?.id) return;
        setLoadingHist(true);
        api.get(`/chantier-qualifications/${q.id}/history`)
            .then((r) => setHistory(r.data || []))
            .catch(() => setHistory([]))
            .finally(() => setLoadingHist(false));
    }, [open, q?.id]);

    if (!q) return null;

    const answers = q.answers || {};
    const answerEntries = Object.entries(answers).filter(([k]) => !k.startsWith('_'));

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl max-h-[90vh] p-0 overflow-hidden flex flex-col" data-testid="chantier-qualif-detail-dialog">
                <DialogHeader className="px-4 sm:px-6 pt-5 pb-3 border-b bg-slate-50">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div>
                            <DialogTitle className="flex items-center gap-2 text-slate-900 text-base sm:text-lg">
                                <ClipboardList className="w-5 h-5" />
                                Qualification Travaux d’optimisation — Détail
                            </DialogTitle>
                            <p className="text-xs text-slate-500 mt-1">
                                {q.status === 'validated' && q.validated_at ? `Validé le ${new Date(q.validated_at).toLocaleString('fr-FR')}` : `Brouillon · maj ${new Date(q.updated_at).toLocaleString('fr-FR')}`}
                            </p>
                        </div>
                        <div className="flex items-center gap-2">
                            <Badge className={NOTE_TONE(q.display_note)} data-testid="qualif-detail-note">{q.display_note}/4</Badge>
                            {q.is_provisional && <Badge variant="outline" className="text-[10px]">Provisoire</Badge>}
                        </div>
                    </div>
                </DialogHeader>

                <Tabs defaultValue="synthese" className="flex-1 flex flex-col overflow-hidden">
                    <TabsList className="grid grid-cols-4 mx-4 sm:mx-6 mt-3 shrink-0" data-testid="qualif-detail-tabs">
                        <TabsTrigger value="synthese" data-testid="qualif-tab-synthese" className="text-xs sm:text-sm px-1 sm:px-3">
                            <Sparkles className="w-3.5 h-3.5 sm:mr-1.5" />
                            <span className="hidden sm:inline">Synthèse</span>
                            <span className="sm:hidden ml-1">Synth.</span>
                        </TabsTrigger>
                        <TabsTrigger value="reponses" data-testid="qualif-tab-reponses" className="text-xs sm:text-sm px-1 sm:px-3">
                            <TrendingUp className="w-3.5 h-3.5 sm:mr-1.5" />
                            <span className="ml-1 sm:ml-0">Réponses</span>
                        </TabsTrigger>
                        <TabsTrigger value="documents" data-testid="qualif-tab-documents" className="text-xs sm:text-sm px-1 sm:px-3">
                            <FileText className="w-3.5 h-3.5 sm:mr-1.5" />
                            <span className="hidden sm:inline">Documents</span>
                            <span className="sm:hidden ml-1">Docs</span>
                        </TabsTrigger>
                        <TabsTrigger value="historique" data-testid="qualif-tab-historique" className="text-xs sm:text-sm px-1 sm:px-3">
                            <History className="w-3.5 h-3.5 sm:mr-1.5" />
                            <span className="hidden sm:inline">Historique</span>
                            <span className="sm:hidden ml-1">Hist.</span>
                        </TabsTrigger>
                    </TabsList>

                    <ScrollArea className="flex-1 px-4 sm:px-6 py-4">
                        {/* SYNTHESE */}
                        <TabsContent value="synthese" className="mt-0 space-y-5" data-testid="qualif-panel-synthese">
                            <div>
                                <div className="flex items-center justify-between mb-1.5">
                                    <span className="text-sm font-medium text-slate-700">Complétude</span>
                                    <span className="text-sm text-slate-600">{q.completeness}%</span>
                                </div>
                                <Progress value={q.completeness || 0} className="h-2" />
                                <p className="text-xs text-slate-500 mt-1.5">
                                    Score interne : <b>{q.internal_score}/100</b>
                                </p>
                            </div>
                            <Separator />
                            <div>
                                <h4 className="text-sm font-semibold text-slate-800 mb-2">Appréciation</h4>
                                <p className="text-sm text-slate-700 leading-relaxed">{q.appreciation}</p>
                            </div>
                            {q.leviers?.length > 0 && (
                                <div>
                                    <h4 className="text-sm font-semibold text-slate-800 mb-2 flex items-center gap-1.5">
                                        <TrendingUp className="w-4 h-4 text-emerald-600" /> Leviers identifiés
                                    </h4>
                                    <ul className="text-sm text-slate-700 space-y-1 pl-1">
                                        {q.leviers.map((l, i) => (
                                            <li key={i} className="flex gap-2"><span className="text-emerald-600">•</span><span>{l}</span></li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            {q.recommendations?.length > 0 && (
                                <div>
                                    <h4 className="text-sm font-semibold text-slate-800 mb-2 flex items-center gap-1.5">
                                        <Sparkles className="w-4 h-4 text-blue-600" /> Recommandations
                                    </h4>
                                    <ul className="text-sm text-slate-700 space-y-1 pl-1">
                                        {q.recommendations.map((r, i) => (
                                            <li key={i} className="flex gap-2"><span className="text-blue-600">→</span><span>{r}</span></li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            {q.missing_information?.length > 0 && (
                                <div>
                                    <h4 className="text-sm font-semibold text-slate-800 mb-2 flex items-center gap-1.5">
                                        <AlertTriangle className="w-4 h-4 text-amber-600" /> Informations manquantes
                                    </h4>
                                    <ul className="text-sm text-slate-700 space-y-1 pl-1">
                                        {q.missing_information.map((m, i) => (
                                            <li key={i} className="flex gap-2"><span className="text-amber-600">•</span><span>{m}</span></li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            {/* Note importante — cette qualification n'est qu'une pré-étude */}
                            <div className="rounded-md bg-slate-100 border border-slate-200 px-3 py-2 mt-2" data-testid="qualif-detail-disclaimer">
                                <p className="text-[11px] text-slate-500 leading-relaxed italic">
                                    Cette qualification donne une première estimation du potentiel.
                                    La rentabilité définitive d&apos;un dossier ne peut être confirmée qu&apos;après
                                    l&apos;étude technique complète réalisée par le bureau d&apos;études.
                                </p>
                            </div>
                        </TabsContent>

                        {/* REPONSES */}
                        <TabsContent value="reponses" className="mt-0" data-testid="qualif-panel-reponses">
                            {answerEntries.length === 0 ? (
                                <p className="text-sm text-slate-500 italic">Aucune réponse enregistrée.</p>
                            ) : (
                                <div className="space-y-2">
                                    {answerEntries.map(([k, v]) => (
                                        <div key={k} className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-1 sm:gap-4 py-1.5 border-b border-slate-100 last:border-0">
                                            <span className="text-xs text-slate-500 flex-shrink-0 sm:min-w-[35%]">{labelKey(k)}</span>
                                            <span className="text-sm text-slate-800 sm:text-right break-words">{labelValue(v)}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </TabsContent>

                        {/* DOCUMENTS */}
                        <TabsContent value="documents" className="mt-0" data-testid="qualif-panel-documents-wrapper">
                            <ChantierQualificationDocumentsTab
                                qualification={q}
                                currentUser={currentUser}
                                canEdit={canEdit}
                            />
                        </TabsContent>

                        {/* HISTORIQUE */}
                        <TabsContent value="historique" className="mt-0" data-testid="qualif-panel-historique">
                            {loadingHist ? (
                                <div className="flex items-center gap-2 text-sm text-slate-500 py-6 justify-center">
                                    <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
                                </div>
                            ) : history.length === 0 ? (
                                <p className="text-sm text-slate-500 italic">Aucun événement enregistré.</p>
                            ) : (
                                <div className="space-y-3">
                                    {history.map((h) => (
                                        <div key={h.id} className="border border-slate-200 rounded-md p-3 bg-white">
                                            <div className="flex items-center justify-between mb-1">
                                                <span className="text-xs font-medium text-slate-700 uppercase">{h.change_reason || 'modification'}</span>
                                                <span className="text-xs text-slate-500">{new Date(h.created_at).toLocaleString('fr-FR')}</span>
                                            </div>
                                            <p className="text-xs text-slate-500">
                                                {h.change_reason === 'création' && 'Qualification créée.'}
                                                {h.change_reason === 'autosave' && `Réponses mises à jour (${Object.keys(h.new_values?.answers || {}).length} champs).`}
                                                {h.change_reason === 'validation' && 'Qualification validée.'}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </TabsContent>
                    </ScrollArea>
                </Tabs>
            </DialogContent>
        </Dialog>
    );
}
